import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Typography, Box, Card, CardContent, Grid, Button,
  Chip, Switch, FormControlLabel, Avatar, CircularProgress, Alert,
  Paper, Divider, Dialog, DialogTitle, DialogContent, DialogActions,
  LinearProgress, Snackbar,
} from '@mui/material';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import CheckCircleIcon    from '@mui/icons-material/CheckCircle';
import AttachMoneyIcon    from '@mui/icons-material/AttachMoney';
import TrendingUpIcon     from '@mui/icons-material/TrendingUp';
import LocationOnIcon     from '@mui/icons-material/LocationOn';
import NavigationIcon     from '@mui/icons-material/Navigation';
import CloseIcon          from '@mui/icons-material/Close';
import EuroIcon           from '@mui/icons-material/Euro';
import TimerIcon          from '@mui/icons-material/Timer';
import MyLocationIcon     from '@mui/icons-material/MyLocation';
import WifiOffIcon        from '@mui/icons-material/WifiOff';
import WarningAmberIcon    from '@mui/icons-material/WarningAmber';
import UploadFileIcon     from '@mui/icons-material/UploadFile';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import TwoWheelerIcon     from '@mui/icons-material/TwoWheeler';
import restaurantService  from '../../services/restaurant.service';
import partnerService     from '../../services/partner.service';
import realtimeService    from '../../services/realtime.service';
import { useAuth }        from '../../context/AuthContext';
import { TZ } from '../../utils/date';

const OFFER_TIMEOUT = 60; // secondes

// ── Compte à rebours circulaire ───────────────────────────────────────────────
const CountdownTimer = ({ seconds, total }) => {
  const pct = (seconds / total) * 100;
  const color = seconds > 20 ? '#4caf50' : seconds > 10 ? '#ff9800' : '#f44336';
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <CircularProgress
        variant="determinate"
        value={pct}
        size={80}
        thickness={5}
        sx={{ color }}
      />
      <Box sx={{
        top: 0, left: 0, bottom: 0, right: 0,
        position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Typography variant="h5" fontWeight={700} sx={{ color }}>
          {seconds}
        </Typography>
      </Box>
    </Box>
  );
};

// ── Carte commande active ─────────────────────────────────────────────────────
const ActiveOrderCard = ({ order, onNavigate }) => (
  <Card sx={{ mb: 3, border: '2px solid', borderColor: 'primary.main', bgcolor: 'rgba(85,37,131,0.05)' }}>
    <CardContent>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <DeliveryDiningIcon color="primary" />
          <Typography variant="h6" fontWeight={700}>Livraison en cours</Typography>
        </Box>
        <Chip
          label={`+${(parseFloat(order.delivery_fee) * 0.95).toFixed(2)} €`}
          color="success"
          sx={{ fontWeight: 700 }}
        />
      </Box>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        #{order.order_number} — {order.restaurant_name}
      </Typography>
      <Divider sx={{ my: 1.5 }} />
      <Box display="flex" alignItems="flex-start" gap={1} mb={1}>
        <LocationOnIcon fontSize="small" color="primary" sx={{ mt: 0.3 }} />
        <Typography variant="body2">{order.delivery_address}</Typography>
      </Box>
      <Button
        fullWidth variant="contained" size="large"
        startIcon={<NavigationIcon />}
        onClick={() => onNavigate(order.id)}
        sx={{ mt: 2 }}
      >
        Reprendre la livraison
      </Button>
    </CardContent>
  </Card>
);

// ─────────────────────────────────────────────────────────────────────────────
const DeliveryDashboard = () => {
  const navigate  = useNavigate();
  const { user }  = useAuth();

  const [isOnDuty,     setIsOnDuty]     = useState(false);
  const [dutyLoading,  setDutyLoading]  = useState(false);
  const [gpsActive,    setGpsActive]    = useState(false);
  const [stats,        setStats]        = useState(null);
  const [activeOrder,  setActiveOrder]  = useState(null);
  const [loading,      setLoading]      = useState(true);

  // Offre de livraison reçue via socket
  const [offer,        setOffer]        = useState(null); // { orderId, orderNumber, restaurantName, restaurantAddress, deliveryAddress, deliveryFee, distanceKm, expiresIn }
  const [countdown,    setCountdown]    = useState(0);
  const [offerLoading, setOfferLoading] = useState(false);

  const [snack,        setSnack]        = useState({ open: false, msg: '', sev: 'info' });

  // KBIS
  const [kbisInfo,     setKbisInfo]     = useState(null); // { deadline, status, is_suspended, days_remaining }
  const [kbisUploading, setKbisUploading] = useState(false);

  const watchIdRef    = useRef(null);
  const countdownRef  = useRef(null);
  const locationPushRef = useRef(null);

  // ── Charge les données au montage ─────────────────────────────────────────
  const loadData = useCallback(async () => {
    try {
      // Each call is independent — one failure must not block the others
      const [statsRes, activeRes, pendingRes] = await Promise.allSettled([
        restaurantService.getDriverStats(),
        restaurantService.getDriverActiveOrder(),
        restaurantService.getDriverPendingOffer(),
      ]);
      if (statsRes.status === 'fulfilled' && statsRes.value.success) {
        setStats(statsRes.value.stats);
        setIsOnDuty(!!statsRes.value.stats.is_on_duty);
        if (statsRes.value.kbis) setKbisInfo(statsRes.value.kbis);
      }
      if (activeRes.status === 'fulfilled' && activeRes.value.success) {
        setActiveOrder(activeRes.value.order);
      }
      // Show pending offer if driver has one waiting
      if (pendingRes.status === 'fulfilled' && pendingRes.value.success && pendingRes.value.offer) {
        setOffer(pendingRes.value.offer);
        setCountdown(pendingRes.value.offer.expiresIn || OFFER_TIMEOUT);
      }
    } catch {
      // silencieux
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Socket.io : connexion + écoute des offres ─────────────────────────────
  useEffect(() => {
    realtimeService.connect();

    const handleOffer = (data) => {
      setOffer(data);
      setCountdown(data.expiresIn || OFFER_TIMEOUT);
    };

    realtimeService.onDeliveryOffer(handleOffer);

    loadData();

    return () => {
      realtimeService.off('delivery-offer');
    };
  }, [loadData]);

  // ── Compte à rebours de l'offre ───────────────────────────────────────────
  useEffect(() => {
    if (!offer) return;

    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          setOffer(null);  // Offre expirée
          setSnack({ open: true, msg: "Offre expirée — le prochain livreur a été contacté", sev: 'warning' });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownRef.current);
  }, [offer]);

  // ── GPS : démarrer le suivi de position ───────────────────────────────────
  const startGps = useCallback(() => {
    if (!navigator.geolocation) return;
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        setGpsActive(true);
        // Push GPS vers le backend toutes les 15s max (throttle)
        if (!locationPushRef.current) {
          locationPushRef.current = true;
          restaurantService.updateDriverLocation(
            pos.coords.latitude,
            pos.coords.longitude
          ).catch(() => {});
          setTimeout(() => { locationPushRef.current = false; }, 15000);
        }
      },
      () => setGpsActive(false),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
    );
  }, []);

  const stopGps = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setGpsActive(false);
  }, []);

  // ── Toggle disponibilité ──────────────────────────────────────────────────
  const handleToggleDuty = async () => {
    setDutyLoading(true);
    const next = !isOnDuty;
    try {
      const res = await restaurantService.toggleDriverDuty(next);
      if (res.success) {
        setIsOnDuty(res.is_on_duty);
        if (res.is_on_duty) {
          startGps();
          setSnack({ open: true, msg: "Vous êtes en service — vous recevrez les offres de livraison", sev: 'success' });
        } else {
          stopGps();
          setSnack({ open: true, msg: "Vous êtes hors service", sev: 'info' });
        }
      }
    } catch {
      setSnack({ open: true, msg: "Erreur réseau", sev: 'error' });
    } finally {
      setDutyLoading(false);
    }
  };

  // ── Accepter une offre ────────────────────────────────────────────────────
  const handleAccept = async () => {
    if (!offer) return;
    setOfferLoading(true);
    clearInterval(countdownRef.current);
    try {
      const res = await restaurantService.acceptDelivery(offer.orderId);
      if (res.success) {
        setOffer(null);
        setSnack({ open: true, msg: "Course acceptée ! En route vers le restaurant.", sev: 'success' });
        // Recharger la commande active
        const activeRes = await restaurantService.getDriverActiveOrder();
        if (activeRes.success) setActiveOrder(activeRes.order);
        navigate(`/delivery/order/${offer.orderId}`);
      } else {
        setSnack({ open: true, msg: res.message || "Course non disponible", sev: 'error' });
        setOffer(null);
      }
    } catch {
      setSnack({ open: true, msg: "Erreur lors de l'acceptation", sev: 'error' });
      setOffer(null);
    } finally {
      setOfferLoading(false);
    }
  };

  // ── Refuser une offre ─────────────────────────────────────────────────────
  const handleRefuse = async () => {
    if (!offer) return;
    clearInterval(countdownRef.current);
    try {
      await restaurantService.refuseDelivery(offer.orderId);
    } catch {}
    setOffer(null);
    setSnack({ open: true, msg: "Course refusée", sev: 'info' });
  };

  // ── Upload KBIS ────────────────────────────────────────────────────────────
  const handleKbisUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setSnack({ open: true, msg: 'Le fichier ne doit pas dépasser 5 Mo', sev: 'error' });
      return;
    }
    setKbisUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const res = await partnerService.uploadKbis(reader.result);
          if (res.success) {
            setSnack({ open: true, msg: 'KBIS soumis ! En attente de validation.', sev: 'success' });
            setKbisInfo(prev => ({ ...prev, status: 'pending' }));
          }
        } catch {
          setSnack({ open: true, msg: 'Erreur lors de l\'envoi du KBIS', sev: 'error' });
        } finally {
          setKbisUploading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch {
      setKbisUploading(false);
      setSnack({ open: true, msg: 'Erreur lors de la lecture du fichier', sev: 'error' });
    }
  };

  // ── Cleanup au démontage ───────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      stopGps();
      clearInterval(countdownRef.current);
    };
  }, [stopGps]);

  // ── Démarrer GPS si déjà en service ───────────────────────────────────────
  useEffect(() => {
    if (isOnDuty) startGps();
  }, [isOnDuty, startGps]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', pb: 6 }}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box sx={{ background: 'linear-gradient(135deg, #552583 0%, #33154f 100%)', color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ width: 60, height: 60, bgcolor: 'rgba(255,255,255,0.2)' }}>
                <DeliveryDiningIcon fontSize="large" />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={700}>
                  Bonjour, {user?.first_name} !
                </Typography>
                <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                  <Box sx={{
                    width: 8, height: 8, borderRadius: '50%',
                    bgcolor: isOnDuty ? '#4caf50' : 'rgba(255,255,255,0.4)',
                    animation: isOnDuty ? 'pulse 1.5s infinite' : 'none',
                    '@keyframes pulse': {
                      '0%,100%': { boxShadow: '0 0 0 0 rgba(76,175,80,0.6)' },
                      '50%': { boxShadow: '0 0 0 6px rgba(76,175,80,0)' },
                    },
                  }} />
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    {isOnDuty ? 'En service' : 'Hors service'}
                    {isOnDuty && gpsActive && ' · GPS actif'}
                    {isOnDuty && !gpsActive && ' · GPS en attente…'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box display="flex" alignItems="center" gap={2}>
              {isOnDuty && !gpsActive && (
                <Chip
                  icon={<WifiOffIcon />}
                  label="GPS requis"
                  size="small"
                  sx={{ bgcolor: 'rgba(255,152,0,0.3)', color: 'white' }}
                />
              )}
              <FormControlLabel
                control={
                  <Switch
                    checked={isOnDuty}
                    onChange={handleToggleDuty}
                    disabled={dutyLoading}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#4caf50' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#4caf50' },
                    }}
                  />
                }
                label={isOnDuty ? 'En service' : 'Hors service'}
                sx={{ color: 'white', mr: 0 }}
              />
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: -2 }}>

        {/* ── Alerte KBIS ──────────────────────────────────────────────────── */}
        {kbisInfo && kbisInfo.deadline && kbisInfo.status !== 'approved' && (
          <Box sx={{
            mt: 3, mb: 2, p: 3, borderRadius: '16px',
            bgcolor: kbisInfo.is_suspended ? 'rgba(244,67,54,0.08)' : kbisInfo.days_remaining <= 7 ? 'rgba(255,152,0,0.08)' : 'rgba(85,37,131,0.06)',
            border: `2px solid ${kbisInfo.is_suspended ? '#f44336' : kbisInfo.days_remaining <= 7 ? '#ff9800' : '#552583'}`,
          }}>
            <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
              <WarningAmberIcon sx={{ color: kbisInfo.is_suspended ? '#f44336' : kbisInfo.days_remaining <= 7 ? '#ff9800' : '#552583' }} />
              <Typography variant="h6" fontWeight={700} sx={{ color: kbisInfo.is_suspended ? '#f44336' : '#1a1a1a' }}>
                {kbisInfo.is_suspended ? 'Compte suspendu — KBIS non fourni' : 'Extrait KBIS requis'}
              </Typography>
            </Box>

            {kbisInfo.status === 'pending' ? (
              <Alert severity="info" sx={{ mb: 2, borderRadius: '10px' }}>
                Votre KBIS a été soumis et est en cours de vérification par un administrateur.
              </Alert>
            ) : kbisInfo.status === 'rejected' ? (
              <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
                Votre KBIS a été rejeté. Veuillez soumettre un nouveau document conforme.
              </Alert>
            ) : null}

            {!kbisInfo.is_suspended && kbisInfo.status !== 'pending' && (
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Box sx={{
                  px: 2.5, py: 1.5, borderRadius: '12px',
                  bgcolor: kbisInfo.days_remaining <= 7 ? 'rgba(244,67,54,0.1)' : 'rgba(85,37,131,0.08)',
                  border: `1px solid ${kbisInfo.days_remaining <= 7 ? 'rgba(244,67,54,0.3)' : 'rgba(85,37,131,0.15)'}`,
                  textAlign: 'center', minWidth: 80,
                }}>
                  <Typography variant="h3" fontWeight={800} sx={{ color: kbisInfo.days_remaining <= 7 ? '#f44336' : '#552583', lineHeight: 1 }}>
                    {kbisInfo.days_remaining}
                  </Typography>
                  <Typography variant="caption" fontWeight={600} sx={{ color: kbisInfo.days_remaining <= 7 ? '#f44336' : '#552583' }}>
                    jour{kbisInfo.days_remaining > 1 ? 's' : ''} restant{kbisInfo.days_remaining > 1 ? 's' : ''}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: '#4a4a4a' }}>
                    Vous devez fournir votre extrait KBIS avant le{' '}
                    <strong>{new Date(kbisInfo.deadline).toLocaleDateString('fr-FR', { timeZone: TZ, day: 'numeric', month: 'long', year: 'numeric' })}</strong>.
                    {kbisInfo.days_remaining <= 7 && ' Votre compte sera suspendu passé ce délai.'}
                  </Typography>
                </Box>
              </Box>
            )}

            {kbisInfo.is_suspended && kbisInfo.status !== 'pending' && (
              <Typography variant="body2" sx={{ color: '#4a4a4a', mb: 2 }}>
                Votre compte a été suspendu car vous n'avez pas fourni votre KBIS dans le délai imparti.
                Soumettez votre document pour réactiver votre compte.
              </Typography>
            )}

            {kbisInfo.status !== 'pending' && (
              <Box>
                <input type="file" id="kbis-upload" hidden accept="image/*,.pdf" onChange={handleKbisUpload} />
                <label htmlFor="kbis-upload">
                  <Button
                    component="span"
                    variant="contained"
                    startIcon={kbisUploading ? <CircularProgress size={18} color="inherit" /> : <UploadFileIcon />}
                    disabled={kbisUploading}
                    sx={{
                      bgcolor: '#552583', '&:hover': { bgcolor: '#3a1760' },
                      borderRadius: '12px', px: 3, py: 1.2, fontWeight: 700,
                    }}
                  >
                    {kbisUploading ? 'Envoi en cours...' : 'Envoyer mon KBIS'}
                  </Button>
                </label>
                <Typography variant="caption" display="block" sx={{ color: '#7a7a7a', mt: 1 }}>
                  Formats acceptés : JPG, PNG, PDF (max 5 Mo)
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* ── Alerte hors service ───────────────────────────────────────────── */}
        {!isOnDuty && (
          <Alert
            severity="warning"
            sx={{ mb: 3, mt: 3 }}
            action={
              <Button color="inherit" size="small" onClick={handleToggleDuty}>
                Activer
              </Button>
            }
          >
            Vous êtes hors service. Activez le mode "En service" pour recevoir des offres de livraison.
          </Alert>
        )}

        {/* ── Commande active ───────────────────────────────────────────────── */}
        {activeOrder && (
          <Box sx={{ mt: 3 }}>
            <ActiveOrderCard
              order={activeOrder}
              onNavigate={(id) => navigate(`/delivery/order/${id}`)}
            />
          </Box>
        )}

        {/* ── Statistiques ──────────────────────────────────────────────────── */}
        <Grid container spacing={3} sx={{ mt: activeOrder ? 0 : 3, mb: 4 }}>
          {[
            {
              label: "Aujourd'hui",
              value: stats?.today_deliveries ?? 0,
              sub: 'livraisons',
              icon: <DeliveryDiningIcon />,
              color: '#552583',
            },
            {
              label: 'Gains du jour',
              value: `${stats?.today_earnings ?? '0.00'} €`,
              sub: '95% des frais',
              icon: <EuroIcon />,
              color: '#2e7d32',
            },
            {
              label: 'Cette semaine',
              value: stats?.week_deliveries ?? 0,
              sub: 'livraisons',
              icon: <TrendingUpIcon />,
              color: '#1565c0',
            },
            {
              label: 'Gains semaine',
              value: `${stats?.week_earnings ?? '0.00'} €`,
              sub: 'net reversé',
              icon: <AttachMoneyIcon />,
              color: '#e65100',
            },
          ].map((s) => (
            <Grid item xs={12} sm={6} md={3} key={s.label}>
              <Card sx={{ bgcolor: 'white' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {s.label}
                      </Typography>
                      <Typography variant="h4" fontWeight={700} sx={{ color: s.color }}>
                        {s.value}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {s.sub}
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: `${s.color}22`, color: s.color, width: 52, height: 52 }}>
                      {s.icon}
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* ── Info fonctionnement ───────────────────────────────────────────── */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Comment ça fonctionne
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {[
              {
                IconComp: LocationOnIcon, title: 'Partagez votre position',
                desc: 'Activez le mode "En service". Votre GPS est transmis automatiquement.',
              },
              {
                IconComp: NotificationsActiveIcon, title: 'Recevez des offres',
                desc: 'Quand une commande est prête, vous êtes notifié si vous êtes le plus proche (rayon 15 km).',
              },
              {
                IconComp: TimerIcon, title: '60 secondes pour répondre',
                desc: 'Acceptez ou refusez. Sans réponse, l\'offre passe au livreur suivant.',
              },
              {
                IconComp: EuroIcon, title: 'Payé après livraison',
                desc: '95% des frais de livraison virés sur votre IBAN dans les 48h.',
              },
            ].map((item) => (
              <Grid item xs={12} sm={6} key={item.title}>
                <Box display="flex" gap={2} alignItems="flex-start">
                  <item.IconComp sx={{ fontSize: '2rem', color: '#552583' }} />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>{item.title}</Typography>
                    <Typography variant="body2" color="text.secondary">{item.desc}</Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* ── GPS info ─────────────────────────────────────────────────────── */}
        {isOnDuty && (
          <Alert
            severity={gpsActive ? 'success' : 'info'}
            icon={gpsActive ? <MyLocationIcon /> : <LocationOnIcon />}
            sx={{ mb: 3 }}
          >
            {gpsActive
              ? 'Position GPS transmise au serveur. Vous êtes visible pour recevoir des courses.'
              : 'En attente du signal GPS… Assurez-vous que la géolocalisation est autorisée dans votre navigateur.'}
          </Alert>
        )}

        {/* ── Actions ───────────────────────────────────────────────────────── */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Button
              fullWidth variant="outlined" size="large"
              onClick={() => navigate('/delivery/history')}
              startIcon={<CheckCircleIcon />}
            >
              Historique des livraisons
            </Button>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button
              fullWidth variant="outlined" size="large"
              onClick={() => navigate('/profile')}
              startIcon={<EuroIcon />}
            >
              Mon IBAN (paiements)
            </Button>
          </Grid>
        </Grid>
      </Container>

      {/* ── Dialog : Offre de livraison ───────────────────────────────────── */}
      <Dialog
        open={!!offer}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: '2px solid',
            borderColor: countdown > 20 ? 'success.main' : countdown > 10 ? 'warning.main' : 'error.main',
            transition: 'border-color 0.5s',
          },
        }}
      >
        {offer && (
          <>
            <DialogTitle sx={{ pb: 0 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="h6" fontWeight={700}><TwoWheelerIcon sx={{ fontSize: 20, mr: 0.5, verticalAlign: 'text-bottom' }} /> Nouvelle course !</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Commande #{offer.orderNumber}
                  </Typography>
                </Box>
                <CountdownTimer seconds={countdown} total={OFFER_TIMEOUT} />
              </Box>
            </DialogTitle>

            <LinearProgress
              variant="determinate"
              value={(countdown / OFFER_TIMEOUT) * 100}
              sx={{
                mx: 3,
                bgcolor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  bgcolor: countdown > 20 ? 'success.main' : countdown > 10 ? 'warning.main' : 'error.main',
                  transition: 'background-color 0.5s',
                },
              }}
            />

            <DialogContent sx={{ pt: 2 }}>
              {/* Gain */}
              <Box
                sx={{
                  textAlign: 'center', py: 2, mb: 2,
                  bgcolor: 'success.light', borderRadius: 2,
                }}
              >
                <Typography variant="h4" fontWeight={800} color="success.dark">
                  +{(offer.deliveryFee * 0.95).toFixed(2)} €
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  (frais de livraison × 95%)
                </Typography>
              </Box>

              {/* Détails */}
              <Box sx={{ mb: 1.5 }}>
                <Box display="flex" alignItems="flex-start" gap={1.5} mb={1.5}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light', fontSize: 14 }}>R</Avatar>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Restaurant — récupération</Typography>
                    <Typography variant="body2" fontWeight={600}>{offer.restaurantName}</Typography>
                    <Typography variant="body2" color="text.secondary">{offer.restaurantAddress}</Typography>
                  </Box>
                </Box>

                <Box display="flex" alignItems="flex-start" gap={1.5}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'error.light', fontSize: 14 }}>C</Avatar>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Client — livraison</Typography>
                    <Typography variant="body2" fontWeight={600}>{offer.deliveryAddress}</Typography>
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box display="flex" gap={2} justifyContent="center">
                <Chip
                  icon={<LocationOnIcon />}
                  label={`${offer.distanceKm} km de vous`}
                  size="medium"
                  color="default"
                />
                <Chip
                  icon={<TimerIcon />}
                  label={`${countdown}s restantes`}
                  size="medium"
                  color={countdown > 20 ? 'success' : countdown > 10 ? 'warning' : 'error'}
                />
              </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                color="error"
                size="large"
                startIcon={<CloseIcon />}
                onClick={handleRefuse}
                disabled={offerLoading}
              >
                Refuser
              </Button>
              <Button
                fullWidth
                variant="contained"
                color="success"
                size="large"
                startIcon={offerLoading ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                onClick={handleAccept}
                disabled={offerLoading}
              >
                Accepter
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── Snackbar ──────────────────────────────────────────────────────── */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.sev} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DeliveryDashboard;
